// Voice activity detection hook backed by Silero VAD ONNX via
// `@ricky0123/vad-web`. The package + its ONNX weights (~2 MB total)
// are **lazy-loaded** on the first activation so they don't inflate
// the initial JS bundle — the dynamic `import("@ricky0123/vad-web")`
// runs in parallel with the parent's `getUserMedia` prompt, and the
// browser caches the weights for subsequent activations.
//
// The hook drives a small state machine for the chat dictation button:
//   idle → loading → speaking ⇄ silence → (onSilence) → idle
// and exposes `silenceProgress` (0..1) so the consumer can render a
// countdown ring during the silence window before auto-shutoff fires.

import { useEffect, useRef, useState } from "react";

export type VadState = "idle" | "loading" | "speaking" | "silence";

interface UseVadOptions {
  stream: MediaStream | null;
  audioContext: AudioContext | null;
  silenceMs: number;
  onSilence: () => void;
}

interface UseVadReturn {
  state: VadState;
  silenceProgress: number;
}

interface MicVadInstance {
  start: () => Promise<void>;
  pause: () => Promise<void>;
  destroy: () => Promise<void>;
}

const PROGRESS_TICK_MS = 100;

export function useVad(options: UseVadOptions): UseVadReturn {
  const { stream, audioContext, silenceMs, onSilence } = options;
  const [state, setState] = useState<VadState>("idle");
  const [silenceProgress, setSilenceProgress] = useState(0);

  // Refs hold the things React shouldn't observe — they survive re-renders
  // without retriggering effects.
  const micRef = useRef<MicVadInstance | null>(null);
  const silenceStartRef = useRef<number | null>(null);
  const onSilenceRef = useRef(onSilence);
  // Avoid re-running the effect when the consumer rotates the callback
  // identity. The current onSilence is read off the ref inside the timer.
  useEffect(() => {
    onSilenceRef.current = onSilence;
  }, [onSilence]);

  useEffect(() => {
    if (stream === null || audioContext === null) {
      setState("idle");
      setSilenceProgress(0);
      return;
    }

    let cancelled = false;
    setState("loading");

    (async () => {
      try {
        const mod = await import("@ricky0123/vad-web");
        if (cancelled) return;
        const mic = await mod.MicVAD.new({
          audioContext,
          getStream: async () => stream,
          // Asset hosting paths: the Silero ONNX weights, the VAD worklet
          // bundle, and the onnxruntime-web WASM all live under
          // `/vad/` (copied from node_modules into `public/vad/` so the
          // dev server + the prod build both serve them as static
          // assets, with no Vite transform pipeline involvement).
          baseAssetPath: "/vad/",
          onnxWASMBasePath: "/vad/",
          // Force single-threaded onnxruntime-web. The "threaded" WASM
          // build is the only variant `onnxruntime-web/wasm` ships, but
          // it can run pthread-free when `numThreads === 1` — that
          // avoids the `SharedArrayBuffer` requirement, which in turn
          // avoids the COOP/COEP cross-origin-isolation headers we'd
          // otherwise have to set on every served origin. vad-web calls
          // this hook AFTER setting `wasmPaths` and BEFORE the first
          // `InferenceSession.create()`, so the value lands in time.
          ortConfig: (ortInstance) => {
            ortInstance.env.wasm.numThreads = 1;
            ortInstance.env.logLevel = "error";
          },
          // Linear PCM at 16 kHz internally — the package handles the
          // resampling from the source MediaStream regardless of its
          // native rate.
          model: "legacy",
          positiveSpeechThreshold: 0.7,
          negativeSpeechThreshold: 0.35,
          minSpeechMs: 96,
          onSpeechStart: () => {
            silenceStartRef.current = null;
            setSilenceProgress(0);
            setState("speaking");
          },
          onSpeechEnd: () => {
            silenceStartRef.current = performance.now();
            setState("silence");
          },
          onVADMisfire: () => {
            // Short blip; treat as if we're still in the prior state.
          },
        });
        if (cancelled) {
          await mic.destroy();
          return;
        }
        micRef.current = mic;
        await mic.start();
        // Silero defaults: between activations the hook starts in
        // "silence" so the auto-shutoff timer is armed for the
        // "user clicked the mic and then didn't say anything" case.
        silenceStartRef.current = performance.now();
        setState("silence");
      } catch (_err) {
        if (!cancelled) {
          setState("idle");
        }
      }
    })();

    return () => {
      cancelled = true;
      const mic = micRef.current;
      micRef.current = null;
      if (mic) {
        // Fire-and-forget destroy — the cleanup completes asynchronously
        // and we don't need to await it before the next effect run.
        void mic.destroy();
      }
      silenceStartRef.current = null;
      setSilenceProgress(0);
    };
  }, [stream, audioContext]);

  // Silence-timer ticker: advances `silenceProgress` and fires
  // `onSilence` exactly once per silence run.
  useEffect(() => {
    if (state !== "silence" || silenceMs <= 0) {
      return;
    }
    const interval = window.setInterval(() => {
      const startedAt = silenceStartRef.current;
      if (startedAt === null) {
        setSilenceProgress(0);
        return;
      }
      const elapsed = performance.now() - startedAt;
      const fraction = Math.min(1, elapsed / silenceMs);
      setSilenceProgress(fraction);
      if (fraction >= 1) {
        window.clearInterval(interval);
        silenceStartRef.current = null;
        onSilenceRef.current();
      }
    }, PROGRESS_TICK_MS);
    return () => window.clearInterval(interval);
  }, [state, silenceMs]);

  return { state, silenceProgress };
}
